"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, PanInfo, useAnimation } from "framer-motion";
import ProductCard from "@/components/products/product-card";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Product {
  id: number;
  name: string;
  price: number;
  slug: string;
  stock: number;
  description: string;
  image_url: string;
  sku?: string;
  has_variants: boolean;
}

export default function FeaturedProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();
  const [containerWidth, setContainerWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  // For drag/swipe functionality
  const x = useMotionValue(0);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('featured', true)
          .limit(12);

        if (error) throw error;

        if (data) {
          setProducts(data);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        // Fallback to sample data if Supabase isn't connected yet
        setProducts([
          {
            id: 1,
            name: "Glenlivet 18 Year Old",
            price: 25000,
            slug: "glenlivet-18-year-old",
            stock: 15,
            description: "A truly special whisky matured for at least 18 years.",
            image_url: "https://images.pexels.com/photos/602750/pexels-photo-602750.jpeg?auto=compress&cs=tinysrgb&w=600",
            sku: "GL18",
            has_variants: false
          },
          {
            id: 2,
            name: "Cloudy Bay Sauvignon Blanc",
            price: 12500,
            slug: "cloudy-bay-sauvignon-blanc",
            stock: 25,
            description: "Vibrant and expressive with a perfect balance of tropical fruit flavors.",
            image_url: "https://images.pexels.com/photos/1545529/pexels-photo-1545529.jpeg?auto=compress&cs=tinysrgb&w=600",
            sku: "CB125",
            has_variants: false
          },
          {
            id: 3,
            name: "Hendrick's Gin",
            price: 15000,
            slug: "hendricks-gin",
            stock: 30,
            description: "A most unusual gin infused with rose petals and cucumber.",
            image_url: "https://images.pexels.com/photos/5947019/pexels-photo-5947019.jpeg?auto=compress&cs=tinysrgb&w=600",
            sku: "HG15",
            has_variants: false
          },
          {
            id: 4,
            name: "Dom Pérignon Vintage",
            price: 95000,
            slug: "dom-perignon-vintage",
            stock: 8,
            description: "Elegant and persistent champagne with remarkable complexity.",
            image_url: "https://images.pexels.com/photos/2775860/pexels-photo-2775860.jpeg?auto=compress&cs=tinysrgb&w=600",
            sku: "DPV15",
            has_variants: false
          },
          {
            id: 5,
            name: "Jack Daniel's Old No. 7",
            price: 10000,
            slug: "jack-daniels-old-no-7",
            stock: 40,
            description: "Charcoal mellowed drop by drop to achieve a smooth and refined Tennessee whiskey.",
            image_url: "https://images.pexels.com/photos/339696/pexels-photo-339696.jpeg?auto=compress&cs=tinysrgb&w=600",
            sku: "JDON7",
            has_variants: false
          },
          {
            id: 6,
            name: "Veuve Clicquot Brut",
            price: 18500,
            slug: "veuve-clicquot-brut",
            stock: 22,
            description: "A classic champagne with a fine balance of strength, silkiness and freshness.",
            image_url: "https://images.pexels.com/photos/4667183/pexels-photo-4667183.jpeg?auto=compress&cs=tinysrgb&w=600",
            sku: "VCB15",
            has_variants: false
          },
          {
            id: 7,
            name: "Grey Goose Vodka",
            price: 12000,
            slug: "grey-goose-vodka",
            stock: 35,
            description: "Made with premium French ingredients for a smooth, extraordinary taste.",
            image_url: "https://images.pexels.com/photos/2171795/pexels-photo-2171795.jpeg?auto=compress&cs=tinysrgb&w=600",
            sku: "GGV15",
            has_variants: false
          },
          {
            id: 8,
            name: "Johnnie Walker Blue Label",
            price: 65000,
            slug: "johnnie-walker-blue-label",
            stock: 10,
            description: "A blend of the rarest and most exceptional whiskies, with a remarkably smooth character.",
            image_url: "https://images.pexels.com/photos/3819969/pexels-photo-3819969.jpeg?auto=compress&cs=tinysrgb&w=600",
            sku: "JWBL15",
            has_variants: false
          },
          {
            id: 9,
            name: "Rémy Martin XO",
            price: 55000,
            slug: "remy-martin-xo",
            stock: 15,
            description: "An opulent blend of eaux-de-vie with exceptional aromatic richness.",
            image_url: "https://images.pexels.com/photos/3103881/pexels-photo-3103881.jpeg?auto=compress&cs=tinysrgb&w=600",
            sku: "RMXO15",
            has_variants: false
          },
          {
            id: 10,
            name: "Bombay Sapphire Gin",
            price: 8500,
            slug: "bombay-sapphire-gin",
            stock: 28,
            description: "Infused with 10 hand-selected exotic botanicals for a distinctive and balanced flavor.",
            image_url: "https://images.pexels.com/photos/341360/pexels-photo-341360.jpeg?auto=compress&cs=tinysrgb&w=600",
            sku: "BSG15",
            has_variants: false
          },
          {
            id: 11,
            name: "Don Julio 1942 Tequila",
            price: 35000,
            slug: "don-julio-1942-tequila",
            stock: 18,
            description: "Handcrafted in tribute to the year Don Julio González began his tequila-making journey.",
            image_url: "https://images.pexels.com/photos/1304472/pexels-photo-1304472.jpeg?auto=compress&cs=tinysrgb&w=600",
            sku: "DJ1942",
            has_variants: false
          },
          {
            id: 12,
            name: "Hennessy XO Cognac",
            price: 60000,
            slug: "hennessy-xo-cognac",
            stock: 12,
            description: "Rich, complex and consistently balanced with persistent flavor.",
            image_url: "https://images.pexels.com/photos/2531188/pexels-photo-2531188.jpeg?auto=compress&cs=tinysrgb&w=600",
            sku: "HXO12",
            has_variants: false
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Calculate how many products to show per slide based on screen size
  const getProductsPerSlide = () => {
    if (typeof window === 'undefined') return 4; // Default for SSR
    if (window.innerWidth < 640) return 2; // Mobile
    if (window.innerWidth < 768) return 2; // Small tablet
    if (window.innerWidth < 1024) return 3; // Large tablet
    return 4; // Desktop
  };
  
  const [productsPerSlide, setProductsPerSlide] = useState(getProductsPerSlide());
  const [totalSlides, setTotalSlides] = useState(Math.ceil(products.length / productsPerSlide));
  
  // Update container dimensions on mount and resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
        const newProductsPerSlide = getProductsPerSlide();
        setProductsPerSlide(newProductsPerSlide);
        setTotalSlides(Math.ceil(products.length / newProductsPerSlide));
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [products.length]);
  
  // Update position when currentIndex changes or container width changes
  useEffect(() => {
    if (containerWidth > 0) {
      controls.start({
        x: -currentIndex * containerWidth,
        transition: { type: "spring", stiffness: 300, damping: 30 }
      });
    }
  }, [currentIndex, containerWidth, controls]);
  
  // Reset current index when total slides changes
  useEffect(() => {
    if (currentIndex >= totalSlides) {
      setCurrentIndex(totalSlides > 0 ? totalSlides - 1 : 0);
    }
  }, [totalSlides, currentIndex]);
  
  const handleDragEnd = (_e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    setIsDragging(false);
    const threshold = containerWidth / 4; // 25% of container width
    
    if (Math.abs(info.offset.x) > threshold) {
      // If dragged far enough, switch slide
      if (info.offset.x > 0 && currentIndex > 0) {
        // Dragged right - go to previous slide
        prevSlide();
      } else if (info.offset.x < 0 && currentIndex < totalSlides - 1) {
        // Dragged left - go to next slide
        nextSlide();
      } else {
        // Bounce back if at the end
        controls.start({
          x: -currentIndex * containerWidth,
          transition: { type: "spring", stiffness: 300, damping: 30 }
        });
      }
    } else {
      // If not dragged far enough, snap back
      controls.start({
        x: -currentIndex * containerWidth,
        transition: { type: "spring", stiffness: 300, damping: 30 }
      });
    }
  };
  
  const nextSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === totalSlides - 1 ? 0 : prevIndex + 1
    );
  };
  
  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? totalSlides - 1 : prevIndex - 1
    );
  };

  return (
    <section>
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold">Featured Products</h2>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={prevSlide}
            className="rounded-full hidden sm:flex"
            disabled={loading || products.length <= productsPerSlide}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={nextSlide}
            className="rounded-full hidden sm:flex"
            disabled={loading || products.length <= productsPerSlide}
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
          <Button variant="outline" asChild>
            <a href="/products">View All</a>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="h-80 bg-muted rounded-lg animate-pulse"></div>
          ))}
        </div>
      ) : (
        <div 
          ref={containerRef}
          className="overflow-hidden relative cursor-grab"
        >
          <motion.div
            className="w-full" 
            animate={controls}
            drag="x"
            dragConstraints={{ left: -containerWidth * (totalSlides - 1), right: 0 }}
            dragElastic={0.1}
            dragMomentum={true}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={handleDragEnd}
            style={{ touchAction: 'pan-y' }}
          >
            <div className="flex">
              {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                <div 
                  key={slideIndex} 
                  className="min-w-full w-full"
                  style={{ pointerEvents: isDragging ? 'none' : 'auto' }}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 px-1">
                    {products
                      .slice(
                        slideIndex * productsPerSlide, 
                        (slideIndex * productsPerSlide) + productsPerSlide
                      )
                      .map(product => (
                        <div key={product.id} className="h-full">
                          <ProductCard product={product} />
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          
          {/* Mobile swipe indicator */}
          <div className="mt-4 sm:hidden text-center text-sm text-muted-foreground">
            <p>Swipe to see more</p>
          </div>
        </div>
      )}
      
      {/* Slide indicators */}
      {!loading && products.length > productsPerSlide && (
        <div className="flex justify-center mt-4 gap-1">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full ${index === currentIndex ? 'bg-primary' : 'bg-muted'}`}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </div>
      )}
    </section>
  );
}